// Broker-side semantics: the warning colour means "your move".
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { BROKER_REQUEST_LABELS } from '@wasity/shared';
import { Chip, Notice } from '@/components/ui';
import { useI18n } from '@/i18n';
import { api } from '@/lib/api';
import { money } from '@/lib/format';
import type { BR, MembershipPage } from '@/lib/types';

/**
 * Whether this broker may publish: any active membership, any tier, as many listings as they like. The
 * API refuses a publish without one (`membership_inactive`); this lets the screens say so up front
 * instead of after the click. Shares its cache with the membership page. Until it has loaded, publishing
 * reads as allowed — the server still decides.
 */
export function usePublishAllowed(): boolean {
  const q = useQuery({ queryKey: ['broker', 'membership'], queryFn: () => api.get<MembershipPage>('/api/broker/membership') });
  const s = q.data?.slots;
  return !s || (s.membershipStatus === 'active' && s.max > 0);
}

export function MembershipNeededNotice() {
  const { t } = useI18n();
  return (
    <Notice tone="warning">
      {t('broker.listings.needsMembership')} <Link to="/broker/membership">{t('broker.listings.getMembership')}</Link>
    </Notice>
  );
}

export const BROKER_ACTIVE = ['active', 'contract_signed', 'licensed', 'offer_pending'] as const;

export function BrokerBrChip({ br }: { br: BR }) {
  const { t, pick } = useI18n();
  if (br.status === 'offer_pending') return <Chip form="outlined">{t('status.offerAwaitingOwner')}</Chip>;
  if (br.status === 'completed' && br.commissionStatus === 'due') return <Chip form="warning">{t('status.commissionAwaiting')}</Chip>;
  if (br.status === 'completed') return <Chip form="filled">{t('status.commissionDone')}</Chip>;
  if (br.status === 'active' || br.status === 'contract_signed' || br.status === 'licensed') return <Chip form="warning">{pick(BROKER_REQUEST_LABELS[br.status])}</Chip>;
  return <Chip form={BROKER_REQUEST_LABELS[br.status].chip}>{pick(BROKER_REQUEST_LABELS[br.status])}</Chip>;
}

/** What the broker should do next on a request, as a short line. */
export function useNextStep() {
  const { t, locale } = useI18n();
  return (br: BR, hasListing?: boolean): { text: string; muted?: boolean } => {
    switch (br.status) {
      case 'assigned':
        return { text: t('broker.requests.next.acceptFirst') };
      case 'active':
        return { text: t('broker.requests.next.signContract') };
      case 'contract_signed':
        return { text: t('broker.requests.next.registerLicence') };
      case 'licensed':
        return { text: hasListing ? t('broker.requests.next.postOffer') : t('broker.requests.next.listIt') };
      case 'offer_pending':
        return { text: t('broker.requests.next.waitingOwner'), muted: true };
      case 'completed':
        return br.commissionStatus === 'due' ? { text: t('broker.requests.next.transfer', { amount: money(br.commissionHalalas, locale) }) } : { text: '—', muted: true };
      default:
        return { text: '—', muted: true };
    }
  };
}
